// Shared address-input component — ported from
// prooveai-portal-v2/frontend/src/components/telehealthShared.js
// (EMPTY_ADDRESS/StreetAutocomplete/AddressFields). Used by Register.js
// here, and by labariumaireport's Report_Generation.js, so both apps'
// address objects stay structurally identical (both eventually hash
// through the same compute_address_hash shape server-side).
import React from "react";
import axios from "axios";
import {
  Box, Typography, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Autocomplete,
} from "@mui/material";
import { CheckCircleOutline, WarningAmber } from "@mui/icons-material";

export const EMPTY_ADDRESS = { line1: "", city: "", state: "", zip: "", unit: "" };

// 2026-10-15 fix (proper mandatory validation + tailored input fields) —
// best-practice US zip validation, 5 digits with an optional "+4" extension.
export function isValidZip(zip) {
  return /^\d{5}(-\d{4})?$/.test((zip || "").trim());
}

// 2026-10-12 fix — the State field was free-text, which let a typo
// (or a full state name instead of the 2-letter code this app stores/
// hashes everywhere else — see compute_address_hash) silently corrupt
// the address. Standard 50-state + DC list of 2-letter codes, same
// Autocomplete component DynamicRequisitionForm.js/OrderTest.js already
// use elsewhere in this app.
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL",
  "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
  "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH",
  "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

const BRAND = { navy: "#00357a", gold: "#c9a96e" };

function formatAddressForDisplay({ street, secondary, city, state, zip }) {
  return `${street}${secondary ? `, ${secondary}` : ""}, ${city}, ${state} ${zip}`;
}

// Labarium identity plan — SECOND, independent safety net against address
// typos/edits the frontend Smarty autocomplete (StreetAutocomplete, above)
// alone can't catch — that only helps someone who actually picks a
// suggestion and never edits it afterward. Right before a form actually
// submits, this calls the backend's OWN authoritative Smarty validation
// (POST /api/validate-address — same validate_address_with_smarty helper
// compute_address_hash itself calls, preview-only, writes nothing) and
// forces an explicit "is this correct?" confirmation before the caller's
// real submit proceeds. Every address-entry call site in this app
// (Register.js, OrderTest.js, MyPatients.js) and labariumaireport's
// Report_Generation.js uses this exact same hook/dialog, via each app's own
// copy of this file — one confirm UX everywhere, not several.
//
// Usage: const { confirmAddress, dialog } = useAddressConfirm(API_BASE);
// render {dialog} once anywhere in the tree, and right before an existing
// submit handler does its real work, do:
//   const confirmed = await confirmAddress(address);
//   if (!confirmed) return; // user chose "No, let me edit" — don't submit
//   // use `confirmed` (not the original `address`) as the address to send —
//   // it's auto-corrected to Smarty's standardized values when validated,
//   // and left as-typed when Smarty couldn't confirm it.
export function useAddressConfirm(apiBase) {
  const [pending, setPending] = React.useState(null); // { original, validated, standardized, resolve }
  const [checking, setChecking] = React.useState(false);
  // Guards against a double-click firing two overlapping confirmAddress
  // calls — the second setPending would silently overwrite the first and
  // leave its caller's promise unresolved forever. A ref (not state) since
  // this has to be checked synchronously, before any re-render.
  const inFlightRef = React.useRef(false);

  const confirmAddress = React.useCallback(async (address) => {
    if (inFlightRef.current) return null;
    inFlightRef.current = true;
    setChecking(true);
    let validated = false;
    let standardized = null;
    try {
      const res = await axios.post(`${apiBase}/api/validate-address`, {
        street: address.line1, unit: address.unit || "", city: address.city, state: address.state, zip: address.zip,
      });
      validated = !!res.data?.validated;
      standardized = res.data?.standardized || null;
    } catch {
      // Network/server failure — never silently skip the checkpoint; fall
      // through to the "couldn't verify" variant of the SAME dialog so the
      // person still gets a forced confirmation either way.
      validated = false;
      standardized = null;
    } finally {
      setChecking(false);
    }
    try {
      return await new Promise((resolve) => setPending({ original: address, validated, standardized, resolve }));
    } finally {
      inFlightRef.current = false;
    }
  }, [apiBase]);

  const handleConfirm = () => {
    const { original, validated, standardized, resolve } = pending;
    const finalAddress = validated && standardized
      ? { ...original, line1: standardized.street, unit: standardized.secondary || "", city: standardized.city, state: standardized.state, zip: standardized.zip }
      : original;
    setPending(null);
    resolve(finalAddress);
  };

  const handleEdit = () => {
    const { resolve } = pending;
    setPending(null);
    resolve(null);
  };

  const displayAddress = pending && (pending.validated && pending.standardized
    ? formatAddressForDisplay(pending.standardized)
    : formatAddressForDisplay({ street: pending.original.line1, secondary: pending.original.unit, city: pending.original.city, state: pending.original.state, zip: pending.original.zip }));

  const dialog = (
    <Dialog open={!!pending} onClose={handleEdit} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      {pending && (
        <>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800, color: BRAND.navy, fontSize: 17 }}>
            {pending.validated
              ? <CheckCircleOutline sx={{ color: "#22c55e" }} />
              : <WarningAmber sx={{ color: BRAND.gold }} />}
            {pending.validated ? "Confirm this address" : "We couldn't verify this address"}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 14, color: "#475569", mb: 1.5 }}>
              {pending.validated
                ? "We validated this address as:"
                : "It may still be correct (e.g. a new address not yet in postal records), but please double-check it:"}
            </Typography>
            <Typography sx={{
              fontSize: 15, fontWeight: 700, color: "#1e293b", p: 1.5, borderRadius: 2,
              bgcolor: pending.validated ? "rgba(0,53,122,0.05)" : "rgba(201,169,110,0.15)",
              border: pending.validated ? "none" : "1px solid rgba(201,169,110,0.4)",
            }}>
              {displayAddress}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#64748b", mt: 1.5 }}>Is this correct?</Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={handleEdit} sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }}>
              No, let me edit
            </Button>
            <Button onClick={handleConfirm} variant="contained"
              sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND.navy, "&:hover": { bgcolor: "#001f4d" } }}>
              Yes, this is correct
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  return { confirmAddress, dialog, checking };
}

// NPPES NPI verification (2026-10-16, CEO-approved research plan) — same
// forced-confirm-before-submit pattern as useAddressConfirm above, reused
// deliberately rather than invented fresh: a public, rate-limited preview
// endpoint (POST /api/verify-npi) is called right before submit, the
// person sees a "we verified this provider as X — is this you?" (or a
// softer "couldn't verify, continue anyway?") dialog, and their own
// click is threaded back into the real submit payload as
// npi_registry_confirmed — completely separate from the server's own
// recomputed `outcome`, which is NEVER trusted from the client and is
// always re-derived for real at account-creation time
// (_create_clinician_account_record). This NEVER blocks submission
// either way — both buttons let the form proceed.
//
// Usage: const { confirmNpi, dialog: npiConfirmDialog } = useNpiConfirm(API_BASE);
// render {dialog} once anywhere in the tree, and right before an existing
// submit handler does its real work (same placement as confirmAddress):
//   const npiConfirmed = await confirmNpi(npi, firstName, lastName);
//   // npiConfirmed is a boolean to send as npi_registry_confirmed — it
//   // is `true` whenever the person had a dialog and clicked through it
//   // (matched OR "continue anyway"), `false` on "let me edit" or when
//   // npi was blank and no dialog ever showed.
export function useNpiConfirm(apiBase) {
  const [pending, setPending] = React.useState(null); // { outcome, registryName, resolve }
  const [checking, setChecking] = React.useState(false);
  const inFlightRef = React.useRef(false);

  const confirmNpi = React.useCallback(async (npi, firstName, lastName) => {
    const npiDigits = (npi || "").replace(/\D/g, "");
    if (!npiDigits) return false; // nothing to verify — never show a dialog for a blank NPI
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    setChecking(true);
    let outcome = "unavailable";
    let registryName = null;
    try {
      const res = await axios.post(`${apiBase}/api/verify-npi`, {
        npi: npiDigits, first_name: firstName, last_name: lastName,
      });
      outcome = res.data?.outcome || "unavailable";
      registryName = res.data?.registry_name || null;
    } catch {
      // Network/server failure — never silently skip the checkpoint;
      // fall through to the "couldn't verify" dialog variant, same
      // reasoning as useAddressConfirm's own catch block.
      outcome = "unavailable";
      registryName = null;
    } finally {
      setChecking(false);
    }
    try {
      return await new Promise((resolve) => setPending({ outcome, registryName, resolve }));
    } finally {
      inFlightRef.current = false;
    }
  }, [apiBase]);

  const handleContinue = () => {
    const { resolve } = pending;
    setPending(null);
    resolve(true);
  };

  const handleEdit = () => {
    const { resolve } = pending;
    setPending(null);
    resolve(false);
  };

  const matched = pending?.outcome === "matched";

  const dialog = (
    <Dialog open={!!pending} onClose={handleEdit} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      {pending && (
        <>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800, color: BRAND.navy, fontSize: 17 }}>
            {matched
              ? <CheckCircleOutline sx={{ color: "#22c55e" }} />
              : <WarningAmber sx={{ color: BRAND.gold }} />}
            {matched ? "Confirm this provider" : "We couldn't verify this provider"}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 14, color: "#475569", mb: 1.5 }}>
              {matched
                ? "We verified this provider in the NPPES registry as:"
                : "It may still be correct (e.g. the registry may be out of date), but please double-check it:"}
            </Typography>
            {matched && (
              <Typography sx={{
                fontSize: 15, fontWeight: 700, color: "#1e293b", p: 1.5, borderRadius: 2,
                bgcolor: "rgba(0,53,122,0.05)",
              }}>
                Dr. {pending.registryName}
              </Typography>
            )}
            <Typography sx={{ fontSize: 13, color: "#64748b", mt: 1.5 }}>
              {matched ? "Is this you?" : "This never blocks your registration — you can continue either way."}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={handleEdit} sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }}>
              {matched ? "No, let me edit" : "Let me edit"}
            </Button>
            <Button onClick={handleContinue} variant="contained"
              sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND.navy, "&:hover": { bgcolor: "#001f4d" } }}>
              {matched ? "Yes, this is correct" : "Continue anyway"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  return { confirmNpi, dialog, checking };
}

const SMARTY_WEBSITE_KEY = process.env.REACT_APP_SMARTY_WEBSITE_KEY || "";

// Small, reusable required-field label row — same gold-asterisk-normally/
// red-when-missing convention DynamicRequisitionForm.js's FieldLabel
// already established (2026-10-13), reused here (not duplicated with
// different styling) so Order a Test's Step 3 (Patient & Billing) address
// fields read consistently with every other required field in the app.
function RequiredFieldLabel({ label, missing }) {
  return (
    <Box sx={{ mb: 0.4 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: missing ? "#dc2626" : "text.secondary" }}>
        {label}<Box component="span" sx={{ color: missing ? "#dc2626" : BRAND.gold, ml: 0.3 }}>*</Box>
      </Typography>
    </Box>
  );
}

function missingFieldSx(missing) {
  if (!missing) return {};
  return { "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#dc2626 !important", borderWidth: "1.5px" } } };
}

function StreetAutocomplete({ isDark, inputSx, value, onChange, requiredMissing }) {
  const [suggestions, setSuggestions] = React.useState([]);
  // 2026-10-08 — when a street address has more than one known unit,
  // Smarty's first-pass `secondary` field is just a generic placeholder
  // (e.g. the literal word "APT"), not a real unit number — it means
  // "ambiguous, ask again." `unitChoices` holds the real per-unit list
  // from that follow-up lookup, so the person can pick their actual unit
  // instead of either getting a fake value or having to know it blank.
  const [unitChoices, setUnitChoices] = React.useState([]);
  const [unitLoading, setUnitLoading] = React.useState(false);
  // 2026-10-09 — real bug, reported directly: picking a street suggestion
  // sets `line1` to Smarty's own street_line, which almost always differs
  // from what the person actually typed. That line1 change re-triggers
  // THIS effect ~250ms later, repopulating `suggestions` — an absolute,
  // zIndex:10 overlay that then paints on top of the unit-picker box
  // below it (which has no zIndex of its own), silently eating clicks on
  // the real unit chips. This ref lets `choose()` skip exactly one
  // re-fetch for the line1 change IT causes, without disabling the
  // lookup for the person's own subsequent typing.
  const skipNextSuggestLookup = React.useRef(false);

  React.useEffect(() => {
    if (skipNextSuggestLookup.current) { skipNextSuggestLookup.current = false; return undefined; }
    const term = (value.line1 || "").trim();
    if (!SMARTY_WEBSITE_KEY || term.length < 3) { setSuggestions([]); return undefined; }
    const timer = setTimeout(() => {
      const url = `https://us-autocomplete-pro.api.smarty.com/lookup?key=${encodeURIComponent(SMARTY_WEBSITE_KEY)}&search=${encodeURIComponent(term)}&max_results=5`;
      fetch(url).then((r) => (r.ok ? r.json() : { suggestions: [] }))
        .then((d) => setSuggestions(d.suggestions || []))
        .catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [value.line1]);

  const choose = (sg) => {
    // Fixed 2026-10-08 — real bug, reported directly: Smarty's `secondary`
    // field is only a real unit number when `entries` <= 1 (one known
    // unit at this street address). For a multi-unit building, Smarty
    // returns a generic placeholder like "APT" standing in for "multiple
    // units exist, resolve via a follow-up lookup" — taking it as-is
    // copied the literal word "APT" into the unit field as if it were a
    // real apartment number. Only trust it when it's actually specific.
    const hasSpecificUnit = (sg.entries || 0) <= 1;
    skipNextSuggestLookup.current = true;
    onChange({ ...value, line1: sg.street_line || "", city: sg.city || "", state: sg.state || "", zip: sg.zipcode || "", unit: hasSpecificUnit ? (sg.secondary || "") : "" });
    setSuggestions([]);
    setUnitChoices([]);
    if (!hasSpecificUnit) {
      // Follow-up lookup: Smarty returns the real list of units at this
      // specific address when you pass the chosen suggestion back as
      // `selected`, instead of a fresh free-text `search`.
      setUnitLoading(true);
      // Smarty's documented `selected` format (confirmed against Smarty's
      // own US Autocomplete Pro docs, 2026-10-09): NOT a reconstructed,
      // comma-punctuated string — it's "street_line secondary (entries)
      // city state zipcode", e.g. "1042 W Center St Apt (108) Orem UT
      // 84057". The entries COUNT goes in parentheses right after
      // secondary; secondary is still included even when it's just the
      // generic placeholder (e.g. "Apt"). Our old comma-joined string
      // that dropped secondary entirely is what produced Smarty's 422 —
      // a malformed-request response, not "no results."
      const selected = [sg.street_line, sg.secondary, sg.entries ? `(${sg.entries})` : null, sg.city, sg.state, sg.zipcode]
        .filter(Boolean).join(" ");
      const url = `https://us-autocomplete-pro.api.smarty.com/lookup?key=${encodeURIComponent(SMARTY_WEBSITE_KEY)}&search=${encodeURIComponent(sg.street_line || "")}&selected=${encodeURIComponent(selected)}&max_results=10`;
      fetch(url).then((r) => (r.ok ? r.json() : { suggestions: [] }))
        .then((d) => setUnitChoices(d.suggestions || []))
        .catch(() => setUnitChoices([]))
        .finally(() => setUnitLoading(false));
    }
  };

  const chooseUnit = (sg) => {
    onChange({ ...value, unit: sg.secondary || "" });
    setUnitChoices([]);
  };

  return (
    <Box sx={{ position: "relative", mb: 1 }}>
      <TextField hiddenLabel fullWidth placeholder="Street address" sx={{ ...inputSx, ...missingFieldSx(requiredMissing) }}
        value={value.line1} onChange={(e) => onChange({ ...value, line1: e.target.value })} />
      {suggestions.length > 0 && (
        <Box sx={{ position: "absolute", zIndex: 10, left: 0, right: 0, mt: 0.5, bgcolor: isDark ? "#1e293b" : "#fff", border: "1px solid #cbd5e1", borderRadius: 1 }}>
          {suggestions.map((sg, i) => (
            <Box key={i} onMouseDown={(e) => { e.preventDefault(); choose(sg); }}
              sx={{ px: 1.5, py: 1, cursor: "pointer", fontSize: 14, "&:hover": { bgcolor: "rgba(0,0,0,0.06)" } }}>
              {sg.street_line}{(sg.entries || 0) > 1 ? " (multiple units)" : (sg.secondary ? ` ${sg.secondary}` : "")}, {sg.city}, {sg.state} {sg.zipcode}
            </Box>
          ))}
        </Box>
      )}
      {(unitLoading || unitChoices.length > 0) && (
        <Box sx={{ mt: 0.5, p: 1, border: "1px solid #cbd5e1", borderRadius: 1, bgcolor: isDark ? "#1e293b" : "#f8fafc" }}>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 0.5 }}>
            This building has multiple units — select yours:
          </Typography>
          {unitLoading ? (
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Loading units…</Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {unitChoices.map((sg, i) => (
                <Box key={i} onMouseDown={(e) => { e.preventDefault(); chooseUnit(sg); }}
                  sx={{ px: 1.25, py: 0.5, cursor: "pointer", fontSize: 13.5, border: "1px solid #cbd5e1", borderRadius: 1, "&:hover": { bgcolor: "rgba(0,0,0,0.06)" } }}>
                  {sg.secondary || "(no unit number)"}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// 2026-10-08 fix — real gap caught by verification: the default copy
// below ("your account"/"your lab results") was written for a PATIENT
// entering their own address at registration. Order a Test/My Patients
// reuse this exact component for a CLINICIAN entering a PATIENT's
// address — that phrasing reads as a copy-paste artifact in that context.
// `helperText` lets a caller override it; Register.js (the original,
// patient-facing caller) passes nothing and keeps the original copy.
// `showRequired` — new, additive prop (default false, so every existing
// caller — Register.js, MyPatients.js — renders exactly as before). When
// true (OrderTest.js's Step 3, "Patient & Billing"), shows a real gold-
// asterisk label above street/city/state/zip (all 4 ARE enforced by
// canProceedFromStep already — this was the "decorative asterisk" bug's
// mirror image: these fields had NO asterisk at all despite being
// required), switching to red label + red border + "Required." caption
// for whichever of those is actually empty.
export function AddressFields({ isDark, inputSx, value, onChange, helperText, showRequired = false }) {
  const missing = {
    line1: showRequired && !value.line1.trim(),
    city: showRequired && !value.city.trim(),
    // 2026-10-15 fix (proper mandatory validation) — these used to only
    // check non-blank; state/zip can now also be flagged as "missing"
    // (shown as the same red "Required." state) when non-blank but
    // malformed, since a malformed value is just as unusable as a blank one.
    state: showRequired && !US_STATES.includes(value.state),
    zip: showRequired && !isValidZip(value.zip),
  };
  return (
    <>
      <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
        {helperText || "If you have an address on file with us already, use that one. Otherwise, enter your current address — this helps us match your account to your lab results."}
      </Typography>
      {showRequired && <RequiredFieldLabel label="Street Address" missing={missing.line1} />}
      <StreetAutocomplete isDark={isDark} inputSx={inputSx} value={value} onChange={onChange} requiredMissing={missing.line1} />
      {missing.line1 && <Typography sx={{ fontSize: 12, color: "#dc2626", mt: -0.5, mb: 1 }}>Required.</Typography>}
      <TextField hiddenLabel placeholder="Apt / Suite / Unit (optional)" fullWidth sx={{ ...inputSx, mb: 1 }}
        value={value.unit || ""} onChange={(e) => onChange({ ...value, unit: e.target.value })} />
      {showRequired && (
        <Box sx={{ display: "flex", gap: 1, mb: 0.4 }}>
          <Box sx={{ flex: 2 }}><RequiredFieldLabel label="City" missing={missing.city} /></Box>
          <Box sx={{ flex: 1 }}><RequiredFieldLabel label="State" missing={missing.state} /></Box>
          <Box sx={{ flex: 1 }}><RequiredFieldLabel label="ZIP" missing={missing.zip} /></Box>
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField hiddenLabel placeholder="City" sx={{ ...inputSx, ...missingFieldSx(missing.city), flex: 2 }}
          value={value.city} onChange={(e) => onChange({ ...value, city: e.target.value })} />
        <Autocomplete
          options={US_STATES}
          value={value.state || null}
          onChange={(e, newValue) => onChange({ ...value, state: newValue || "" })}
          autoHighlight
          sx={{ flex: 1 }}
          renderInput={(params) => (
            <TextField {...params} hiddenLabel placeholder="State" sx={{ ...inputSx, ...missingFieldSx(missing.state) }} />
          )}
        />
        <TextField hiddenLabel placeholder="ZIP" sx={{ ...inputSx, ...missingFieldSx(missing.zip), flex: 1 }}
          inputProps={{ maxLength: 10, inputMode: "numeric" }}
          value={value.zip} onChange={(e) => onChange({ ...value, zip: e.target.value.replace(/[^\d-]/g, "") })} />
      </Box>
    </>
  );
}
